import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const { FiChevronDown, FiServer, FiMail, FiGlobe, FiLayers, FiHelpCircle, FiSearch } = FiIcons;

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('hosting');
  const [activeItem, setActiveItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'hosting', name: 'Web hosting', icon: FiServer },
    { id: 'email', name: 'Email hosting', icon: FiMail },
    { id: 'design', name: 'Web design', icon: FiLayers },
    { id: 'domains', name: 'Domain services', icon: FiGlobe },
    { id: 'support', name: 'Support & maintenance', icon: FiHelpCircle }
  ];

  const faqData = {
    hosting: [
      {
        question: 'What web hosting plans do you offer?',
        answer: 'We offer various hosting solutions including Basic hosting plan (R1,200/year with 500MB storage), shared hosting for smaller businesses with moderate traffic, custom plans tailored to specific requirements, and fully managed hosting with lightning-fast performance.'
      },
      {
        question: 'What is included in your hosting services?',
        answer: 'Our hosting includes 99.9% uptime guarantee, lightning-fast loading speeds, multi-layered security protection, regular backups and disaster recovery, 24/7 technical support, SSL certificate management, and server maintenance and updates.'
      },
      {
        question: 'How does hosting affect my website\'s SEO performance?',
        answer: 'Website speed, uptime, and security all directly impact search engine rankings. Our optimised hosting environments deliver fast page loading, consistent availability, and robust security – all factors that search engines consider when determining your site\'s ranking position.'
      },
      {
        question: 'Can you help migrate my existing website to your hosting?',
        answer: 'Yes, we provide comprehensive migration services that transfer your existing website, databases, and email accounts to our hosting platform with minimal downtime. Our migration specialists handle all technical aspects, ensuring a smooth transition without disruption to your business.'
      },
      {
        question: 'What security measures do you implement for hosting environments?',
        answer: 'Our multi-layered security includes server firewalls and intrusion detection systems, malware scanning and automated security patching, SSL certificate management, regular security audits, and continuous monitoring and threat prevention.'
      },
      {
        question: 'What is your uptime guarantee?',
        answer: 'We maintain a 99.9% uptime guarantee, backed by robust infrastructure and proactive monitoring systems that detect and address issues before they impact your visitors.'
      }
    ],
    email: [
      {
        question: 'What email hosting services do you provide?',
        answer: 'Our comprehensive email solutions include business email accounts with custom domains, secure email hosting with spam protection, email archiving and backup solutions, webmail access for anywhere connectivity, email migration services, and enterprise-grade functionality and security.'
      },
      {
        question: 'Can I use my own domain for email?',
        answer: 'Yes, we provide business email accounts with your custom domain, which reinforces your brand with every message sent and enhances your professional image.'
      },
      {
        question: 'How secure is your email hosting?',
        answer: 'Our email hosting features advanced security measures including spam filtering, malware protection, encryption, and secure server infrastructure to protect your sensitive business communications.'
      },
      {
        question: 'Do you offer email migration services?',
        answer: 'Yes, we provide seamless email migration services that transition your existing email accounts to our secure platform without losing any data or experiencing significant downtime.'
      },
      {
        question: 'Can I access my email from anywhere?',
        answer: 'Yes, our webmail access allows team members to access email from any location or device with an internet connection, ensuring business continuity and flexibility.'
      }
    ],
    design: [
      {
        question: 'What web design services do you offer?',
        answer: 'Our web design services include custom website design and development, mobile-responsive design implementation, website redesign and modernisation, and SEO-friendly structure development.'
      },
      {
        question: 'What\'s included in your premium web design package?',
        answer: 'The package includes website design of up to 5 pages, client-generated content integration, static layout design, 2 revisions included, mobile-responsive design (1 page = 1024 × 1536 px), SEO-friendly structure. Note: Hosting is excluded and charged separately.'
      },
      {
        question: 'Do you create mobile-responsive websites?',
        answer: 'Yes, all our websites are designed to be fully responsive, ensuring flawless performance across smartphones, tablets, and desktop computers.'
      },
      {
        question: 'Can you redesign my existing website?',
        answer: 'Absolutely! We offer website redesign and modernisation services to bring outdated sites up to current standards and expectations while improving functionality and user experience.'
      },
      {
        question: 'How long does it take to complete a website?',
        answer: 'Project timelines vary depending on complexity and requirements. A standard 5-page website typically takes 2-4 weeks from approval of design concepts to launch, including the 2 revision rounds.'
      }
    ],
    domains: [
      {
        question: 'What domain services do you provide?',
        answer: 'Our comprehensive domain services include domain name registration and availability checking, domain renewal management, domain transfer services, DNS management and configuration, domain privacy protection, and domain portfolio management.'
      },
      {
        question: 'How much does a .co.za domain cost?',
        answer: 'A .co.za domain registration costs R199 per year. We also offer .org.za domains at R199/year and .com domains at R300/year.'
      },
      {
        question: 'Can you help me find the right domain name?',
        answer: 'Yes, we conduct domain searches tailored to your business and help you find the perfect domain name that represents your brand effectively.'
      },
      {
        question: 'Do you offer domain privacy protection?',
        answer: 'Yes, we provide domain privacy protection services that shield your personal information from public view and help reduce spam.'
      },
      {
        question: 'What happens if I forget to renew my domain?',
        answer: 'We offer domain renewal management services to prevent accidental expirations that could compromise your online presence. We will send you timely reminders and can set up automatic renewals.'
      },
      {
        question: 'Can you transfer my existing domain to your management?',
        answer: 'Yes, we provide domain transfer services to consolidate your digital assets under our professional management, ensuring better security and streamlined administration.'
      }
    ],
    support: [
      {
        question: 'What kind of technical support do you provide?',
        answer: 'We offer 24/7 technical support availability, regular system maintenance and updates, security monitoring and threat prevention, performance optimisation, website health monitoring, and proactive issue prevention and rapid response.'
      },
      {
        question: 'How quickly can you resolve technical issues if they arise?',
        answer: 'Our standard response time for critical issues is under 30 minutes, with most problems resolved within 2 hours. Our technical support team is available 24/7, ensuring your business-critical systems receive prompt attention regardless of when issues occur.'
      },
      {
        question: 'Do you provide proactive monitoring?',
        answer: 'Yes, we continuously monitor your systems for performance, security threats, and potential issues. Our proactive approach helps prevent many problems before they occur and ensures optimal system performance.'
      },
      {
        question: 'What if I need help outside business hours?',
        answer: 'Our 24/7 technical support ensures assistance is available whenever you need it, day or night, especially for critical issues that could impact your business operations.'
      },
      {
        question: 'What information do I need to provide to open an account?',
        answer: 'You must provide accurate and complete personal information, current physical residential address (P.O. Boxes not accepted), valid email address and phone number, payment information matching your identity, and business registration details (if applicable).'
      }
    ]
  };

  const toggleItem = (index) => {
    setActiveItem(activeItem === index ? null : index);
  };

  const filteredFAQs = faqData[activeCategory]?.filter(item =>
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Frequently asked questions
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Find answers to common questions about our hosting, email, design, and domain services. Can't find what you're looking for? Contact our support team.
            </p>

            {/* Search */}
            <div className="max-w-md mx-auto relative">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Categories Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setActiveCategory(category.id);
                      setActiveItem(null);
                      setSearchTerm('');
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors duration-200 ${
                      activeCategory === category.id
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <SafeIcon icon={category.icon} className="w-5 h-5" />
                    <span className="font-medium">{category.name}</span>
                  </button>
                ))}
              </div>
            </motion.div>

            {/* FAQ Items */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-8">
                  {categories.find(cat => cat.id === activeCategory)?.name} FAQ
                </h2>

                {filteredFAQs.length === 0 ? (
                  <div className="text-center py-12">
                    <SafeIcon icon={FiHelpCircle} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      {searchTerm ? 'No FAQs found matching your search.' : 'No FAQs available for this category.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredFAQs.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <button
                          onClick={() => toggleItem(index)}
                          className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200"
                        >
                          <span className="font-semibold text-gray-900 pr-4">
                            {item.question}
                          </span>
                          <SafeIcon
                            icon={FiChevronDown}
                            className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                              activeItem === index ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        <AnimatePresence>
                          {activeItem === index && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="px-6 pb-4 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                                {item.answer}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-20" style={{ backgroundColor: '#D14932' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Still have questions?
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Our support team is available 24/7 to help with any questions not covered here. For critical issues, we respond within 30 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:support@primehost.co.za"
                className="bg-white text-red-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                Email support
              </a>
              <a
                href="tel:+27100070690"
                className="border border-red-300 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-600 transition-colors duration-200"
              >
                Call +27 10 007 0690
              </a>
            </div>
            <p className="text-red-100 mt-4 text-sm">
              Business hours: 08:00 - 16:00 daily | Emergency support: 24/7
            </p>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default FAQ;